import { ImagePlus, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/utils/cn';

interface InventoryNewSidebarProps {
  imagePreview: string;
  onPickImage: (file: File | null) => void;
  note?: string;
  onNoteChange?: (note: string) => void;
}

export function InventoryNewSidebar({ imagePreview, onPickImage, note = '', onNoteChange }: InventoryNewSidebarProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onPickImage(file);
    }
  };

  return (
    <aside className="space-y-4 rounded-md border border-slate-200 bg-white p-3">
      {/* Ghi chú thêm */}
      <div className="space-y-2">
        <h2 className="sm:text-sm text-xs text-slate-950 font-medium">Ghi chú thêm</h2>
        <textarea
          value={note}
          maxLength={300}
          onChange={(e) => onNoteChange?.(e.target.value)}
          className="min-h-[100px] w-full rounded-md border border-slate-200 p-2 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none resize-none"
          placeholder="Nhập các thông tin ghi chú khác..."
        />
        <div className="flex justify-end">
          <span className="text-[10px] text-slate-400">{note.length}/300</span>
        </div>
      </div>

      <div className="space-y-2 border-t border-slate-100 pt-2">
        <h2 className="sm:text-sm text-xs text-slate-950 font-medium">Hình ảnh vật tư</h2>
        <label 
          className={cn(
            "flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md border border-dashed p-3 text-center transition-all",
            isDragging ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:bg-slate-50"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {imagePreview ? (
            <div className="relative h-48 w-full group">
              <img src={imagePreview} alt="Xem trước vật tư" className="h-full w-full rounded-md object-cover" />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPickImage(null);
                }}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white transition-transform hover:scale-110 active:scale-95"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <ImagePlus className={cn("h-10 w-10 transition-colors", isDragging ? "text-emerald-500" : "text-slate-400")} />
              <div className="space-y-1">
                <p className="sm:text-sm text-xs text-slate-500 font-medium">Kéo thả ảnh hoặc Chọn ảnh</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-tighter">JPG, PNG, WEBP (Tối đa 5MB)</p>
              </div>
            </>
          )}
          <input
            type="file"
            accept="image/png,image/jpg,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0] || null;
              onPickImage(file);
            }}
          />
        </label>
      </div>

      <div className="space-y-2 border-t border-slate-100 pt-3">
        <h3 className="sm:text-sm text-xs text-slate-900 font-medium">Thông tin nhanh</h3>
        <ul className="space-y-2 text-[11px] text-slate-500">
          <li className="flex items-start gap-2">
            <span className="h-1 w-1 rounded-full bg-slate-300 mt-1.5 shrink-0" />
            <span>Mã vật tư là duy nhất</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="h-1 w-1 rounded-full bg-slate-300 mt-1.5 shrink-0" />
            <span>Vật tư dùng chung cho thi công và bảo dưỡng</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="h-1 w-1 rounded-full bg-slate-300 mt-1.5 shrink-0" />
            <span>Có thể thiết lập tồn kho cảnh báo</span>
          </li>
        </ul>
      </div>
    </aside>
  );
}
